// pages/Cart.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState({});
  const navigate = useNavigate();
  
  // ScrollToTop on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // WhatsApp number
  const whatsappNumber = '919158235932'; // Updated to your WhatsApp number

  // Calculate total price (handling both $ and ₹ formats)
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      // Extract numeric value from price string - handle both $ and ₹
      const dollarMatch = item.price?.match(/\$(\d+(\.\d+)?)/);
      const rupeeMatch = item.price?.match(/₹(\d+(\.\d+)?)/);
      
      if (dollarMatch && dollarMatch[1]) {
        // If price is in dollars, convert to rupees (approximate conversion)
        return total + (parseFloat(dollarMatch[1]) * 75) * (item.quantity || 1); // Using 75 as an approximate conversion rate
      } else if (rupeeMatch && rupeeMatch[1]) {
        // If price is already in rupees
        return total + parseFloat(rupeeMatch[1]) * (item.quantity || 1);
      }
      return total;
    }, 0).toFixed(0); // No decimals for INR
  };

  // Load cart items from localStorage on component mount
  useEffect(() => {
    const loadCart = () => {
      try {
        const savedCart = localStorage.getItem('printTechCart');
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          setCartItems(parsedCart);
          
          // Initialize active image index for each item
          const initialActiveImages = {};
          parsedCart.forEach((item, index) => {
            initialActiveImages[index] = 0; // Start with first image
          });
          setActiveImageIndex(initialActiveImages);
        }
      } catch (error) {
        console.error('Error loading cart:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, []);

  // Save cart items to localStorage whenever cartItems changes
  useEffect(() => {
    if (!loading) {
      localStorage.setItem('printTechCart', JSON.stringify(cartItems));
    }
  }, [cartItems, loading]);

  // Remove item from cart
  const removeItem = (index) => {
    const updatedCart = [...cartItems];
    updatedCart.splice(index, 1);
    setCartItems(updatedCart);
    
    // Update active image indexes
    const updatedActiveImages = { ...activeImageIndex };
    delete updatedActiveImages[index];
    // Renumber remaining indexes
    Object.keys(updatedActiveImages).forEach(key => {
      const keyNum = parseInt(key);
      if (keyNum > index) {
        updatedActiveImages[keyNum - 1] = updatedActiveImages[keyNum];
        delete updatedActiveImages[keyNum];
      }
    });
    setActiveImageIndex(updatedActiveImages);
  };

  // Change item quantity
  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedCart = [...cartItems];
    updatedCart[index] = {
      ...updatedCart[index],
      quantity: newQuantity
    };
    setCartItems(updatedCart);
  };

  // Format currency in INR style
  const formatInrCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Convert displayed prices from $ to ₹ format
  const displayInrPrice = (priceString) => {
    if (!priceString) return '';
    
    // Check if the price is in $ format
    const dollarMatch = priceString.match(/\$(\d+(\.\d+)?)/);
    if (dollarMatch && dollarMatch[1]) {
      // Convert to rupees (approximate)
      const inrAmount = parseFloat(dollarMatch[1]) * 75; // Using 75 as an approximate conversion rate
      return priceString.replace(/\$\d+(\.\d+)?/, formatInrCurrency(inrAmount));
    }
    
    return priceString; // Already in ₹ or another format
  };

  // Handle checkout (WhatsApp integration)
  const handleCheckout = () => {
    // Create message with all items in cart
    const itemsList = cartItems.map(item => 
      `${item.title} (${displayInrPrice(item.price)}) x ${item.quantity || 1}`
    ).join('\n');
    
    const message = `Hello! I would like to order the following items:\n\n${itemsList}\n\nTotal: ${formatInrCurrency(calculateTotal())}`;
    
    // Create WhatsApp URL with your business phone number
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');
  };

  // Calculate item subtotal
  const calculateItemSubtotal = (item) => {
    const quantity = item.quantity || 1;
    const dollarMatch = item.price?.match(/\$(\d+(\.\d+)?)/);
    const rupeeMatch = item.price?.match(/₹(\d+(\.\d+)?)/);
    
    if (dollarMatch && dollarMatch[1]) {
      return formatInrCurrency(parseFloat(dollarMatch[1]) * 75 * quantity);
    } else if (rupeeMatch && rupeeMatch[1]) {
      return formatInrCurrency(parseFloat(rupeeMatch[1]) * quantity);
    }
    
    return '';
  };

  // Handle image cycling
  const handleNextImage = (itemIndex) => {
    const item = cartItems[itemIndex];
    if (!item) return;
    
    // If images array exists, cycle through it
    if (Array.isArray(item.images) && item.images.length > 1) {
      const currentIndex = activeImageIndex[itemIndex] || 0;
      const nextIndex = (currentIndex + 1) % item.images.length;
      setActiveImageIndex({...activeImageIndex, [itemIndex]: nextIndex});
    }
    // If item has image property and images array with multiple images
    else if (item.image && Array.isArray(item.additionalImages) && item.additionalImages.length > 0) {
      const allImages = [item.image, ...item.additionalImages];
      const currentIndex = activeImageIndex[itemIndex] || 0;
      const nextIndex = (currentIndex + 1) % allImages.length;
      setActiveImageIndex({...activeImageIndex, [itemIndex]: nextIndex});
    }
  };

  const handlePrevImage = (itemIndex) => {
    const item = cartItems[itemIndex];
    if (!item) return;
    
    // If images array exists, cycle through it
    if (Array.isArray(item.images) && item.images.length > 1) {
      const currentIndex = activeImageIndex[itemIndex] || 0;
      const prevIndex = (currentIndex - 1 + item.images.length) % item.images.length;
      setActiveImageIndex({...activeImageIndex, [itemIndex]: prevIndex});
    }
    // If item has image property and images array with multiple images
    else if (item.image && Array.isArray(item.additionalImages) && item.additionalImages.length > 0) {
      const allImages = [item.image, ...item.additionalImages];
      const currentIndex = activeImageIndex[itemIndex] || 0;
      const prevIndex = (currentIndex - 1 + allImages.length) % allImages.length;
      setActiveImageIndex({...activeImageIndex, [itemIndex]: prevIndex});
    }
  };

  // Get current image to display
  const getCurrentImage = (item, index) => {
    const currentIndex = activeImageIndex[index] || 0;
    
    // If images array exists
    if (Array.isArray(item.images) && item.images.length > 0) {
      return item.images[currentIndex];
    }
    // If item has image property and additionalImages array
    else if (item.image && Array.isArray(item.additionalImages) && item.additionalImages.length > 0) {
      const allImages = [item.image, ...item.additionalImages];
      return allImages[currentIndex];
    }
    // Default - just return the item image
    return item.image;
  };

  // Check if an item has multiple images
  const hasMultipleImages = (item) => {
    if (Array.isArray(item.images) && item.images.length > 1) {
      return true;
    }
    if (item.image && Array.isArray(item.additionalImages) && item.additionalImages.length > 0) {
      return true;
    }
    return false;
  };

  return (
    <div className="pt-20 pb-16 bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen">
      {/* Cart Header */}
      <div className="bg-gradient-to-r from-black to-red-900 py-10 mb-8 sm:mb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Your Cart
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Review your selected items and proceed to checkout
            </p>
          </div>
        </div>
      </div>
      
      {/* Cart Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
          </div>
        ) : cartItems.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 sm:p-10 text-center max-w-2xl mx-auto">
            <div className="flex justify-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Your cart is empty</h2>
            <p className="text-gray-600 mb-8">Looks like you haven't added any items to your cart yet.</p>
            <Link 
              to="/products"
              className="px-8 py-3 bg-gradient-to-r from-red-700 to-red-500 hover:from-red-800 hover:to-red-600 text-white font-semibold rounded-full transition-colors inline-block shadow-md hover:shadow-lg"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-5 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900">Shopping Cart ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})</h2>
                    <button 
                      onClick={() => setCartItems([])}
                      className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Clear Cart
                    </button>
                  </div>
                </div>
                <ul className="divide-y divide-gray-200">
                  {cartItems.map((item, index) => (
                    <li key={index} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col sm:flex-row items-center gap-4">
                        {/* Product Image with Navigation Controls */}
                        <div className="relative w-28 h-28 bg-gradient-to-br from-red-800 to-black rounded-lg flex-shrink-0 overflow-hidden shadow-md group">
                          {getCurrentImage(item, index) ? (
                            <img 
                              src={getCurrentImage(item, index)} 
                              alt={item.title} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white text-sm">No Image</div>
                          )}
                          
                          {/* Image Navigation Buttons - Only show if multiple images are available */}
                          {hasMultipleImages(item) && (
                            <>
                              <button 
                                onClick={() => handlePrevImage(index)}
                                className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-1 rounded-r opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label="Previous image"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>
                              <button 
                                onClick={() => handleNextImage(index)}
                                className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-1 rounded-l opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label="Next image"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                              
                              {/* Image Counter Indicator */}
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {`${(activeImageIndex[index] || 0) + 1} / ${
                                  Array.isArray(item.images) 
                                    ? item.images.length 
                                    : (Array.isArray(item.additionalImages) 
                                      ? item.additionalImages.length + 1 
                                      : 1)
                                }`}
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Product Details */}
                        <div className="flex-1 text-center sm:text-left">
                          <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                          <p className="text-red-600 font-medium mt-1">{displayInrPrice(item.price)}</p>
                          
                          <div className="flex items-center justify-center sm:justify-start mt-4">
                            <div className="flex items-center bg-gray-100 rounded-full overflow-hidden pr-2">
                              <button 
                                className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors"
                                onClick={() => updateQuantity(index, (item.quantity || 1) - 1)}
                                aria-label="Decrease quantity"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                </svg>
                              </button>
                              <span className="w-8 text-center font-medium">{item.quantity || 1}</span>
                              <button 
                                className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors"
                                onClick={() => updateQuantity(index, (item.quantity || 1) + 1)}
                                aria-label="Increase quantity"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </button>
                            </div>
                            
                            <button 
                              className="ml-4 text-red-500 hover:text-red-700 transition-colors flex items-center text-sm"
                              onClick={() => removeItem(index)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Remove
                            </button>
                          </div>
                        </div>
                        
                        {/* Subtotal - Hide on mobile */}
                        <div className="hidden sm:block text-right">
                          <p className="text-sm text-gray-500">Subtotal</p>
                          <p className="text-lg font-semibold text-gray-900">{calculateItemSubtotal(item)}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Return to Shop - Mobile Friendly */}
              <div className="mt-6 flex justify-center lg:justify-start">
                <Link 
                  to="/products"
                  className="flex items-center text-red-600 hover:text-red-800 transition-colors text-sm font-medium"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Continue Shopping
                </Link>
              </div>
            </div>
            
            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-red-900 to-black rounded-xl shadow-lg p-6 text-white sticky top-24 transition-all transform hover:shadow-xl">
                <h2 className="text-xl font-bold mb-6 border-b border-red-800 pb-2">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-300">
                    <span>Subtotal</span>
                    <span>{formatInrCurrency(calculateTotal())}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Shipping</span>
                    <span>Calculated at checkout</span>
                  </div>
                  <div className="border-t border-red-800 pt-4 mt-4">
                    <div className="flex justify-between font-bold text-white text-lg">
                      <span>Total</span>
                      <span>{formatInrCurrency(calculateTotal())}</span>
                    </div>
                    <p className="text-gray-400 text-xs mt-1 text-right">Includes GST</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <button 
                    onClick={handleCheckout}
                    className="w-full py-3 bg-white text-red-700 font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-md flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                    </svg>
                    Checkout via WhatsApp
                  </button>
                  
                  <button 
                    onClick={() => navigate('/products')}
                    className="w-full py-3 bg-transparent border border-white text-white font-bold rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
                  >
                    Continue Shopping
                  </button>
                </div>
                
                {/* Payment Methods and Security Notice */}
                <div className="mt-6 pt-6 border-t border-red-800">
                  <div className="flex justify-center mb-3">
                    <span className="text-xs text-gray-400">Secure Checkout</span>
                  </div>
                  <div className="flex justify-center items-center space-x-2">
                    <svg className="w-8 h-8 text-gray-300" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Cart;